import { FC, useMemo } from "react";

import { useFormatDate, useT } from "src/internationalization";
import { CaseSummary } from "src/types/case";

import { CaseVersionsDiffTable } from "./CaseVersionsDiffTable";
import { VersionConfig } from "./CaseVersionsDiffTable.types";

export type AlertCaseVersionsDiffModalProps = {
  creationCase: CaseSummary;
  validationCase: CaseSummary | null;
  currentCase: CaseSummary;
  alertNumber: string;
  onClose: () => void;
};

export const AlertCaseVersionsDiffModal: FC<AlertCaseVersionsDiffModalProps> = ({
  onClose,
  creationCase,
  currentCase,
  validationCase,
  alertNumber,
}) => {
  const t = useT();
  const formatDate = useFormatDate();

  // Build versions array in sequential order: creation -> validation -> current
  // The first version (creation) is the baseline, others are compared sequentially
  const versions: VersionConfig[] = useMemo(() => {
    const result: VersionConfig[] = [
      {
        key: "creation",
        label: t("caseVersions.diff.table.scored", {
          date: formatDate(creationCase.snapshotCreation),
        }),
        date: creationCase.snapshotCreation ? new Date(creationCase.snapshotCreation) : null,
        case: creationCase,
      },
    ];

    if (validationCase) {
      result.push({
        key: "validation",
        label: t("caseVersions.diff.table.alertValidated", {
          date: formatDate(validationCase.snapshotCreation),
        }),
        date: validationCase.snapshotCreation ? new Date(validationCase.snapshotCreation) : null,
        case: validationCase,
      });
    }

    result.push({
      key: "current",
      label: t("caseVersions.diff.table.current", {
        date: formatDate(new Date()),
      }),
      date: null,
      case: currentCase,
    });

    return result;
  }, [creationCase, validationCase, currentCase, formatDate, t]);

  return (
    <CaseVersionsDiffTable
      versions={versions}
      title={t("caseVersions.diff.alert.modal.title")}
      subtitle={t("page.alert.title", { number: alertNumber })}
      onClose={onClose}
      searchInputId="searchCaseVersionsDiff"
      tableId="caseVersionsDiff"
    />
  );
};
